import axios from 'axios';
import envConfig from '../../config';
import {
	BidderAccountInvitation,
	BidderAccountCompanyProfile,
	BidderAccountSession,
	BidderAuthResponse,
	BidderEditableCompanyProfile,
	BidderDocument,
	BidderSession,
	ProjectMessage,
	PublicInviteBootstrap,
	VendorCompany,
	VendorContact,
	VendorServiceArea,
	VendorWorkAreaGeometry,
	VendorLocation,
	WorkPerformedType,
} from './types';
import { APP_TOKEN, USER_ROLE } from '../../helpers/Constants';

export const BIDDER_PUBLISH_OPTIONS_EVENT = 'scrubplan:bidder-publish-options';

export interface BidderPublishOptions {
	aiDisclaimer: string;
	includeGroupingBulkAnswers: boolean;
	includeScopeLocations: boolean;
	includeQuoteReview: boolean;
}

const publicClient = axios.create({
	baseURL: envConfig.API_ENDPOINT,
	headers: { 'Content-Type': 'application/json' },
});

const unwrap = <T>(response: any): T => {
	const body = response && response.data !== undefined ? response.data : response;
	return (body && body.data !== undefined ? body.data : body) as T;
};

const sessionHeaders = (sessionToken: string) => ({
	'X-Bidder-Session': sessionToken,
});

const bidderAuthHeaders = () => {
	const token = localStorage.getItem(APP_TOKEN);
	return token ? { 'X-Bidder-Auth': token } : {};
};

const clearBidderInvitationSessions = () => {
	for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
		const key = sessionStorage.key(index);
		if (key && key.indexOf('BIDDER_SESSION_') === 0) sessionStorage.removeItem(key);
	}
};

export const persistBidderAuthentication = (response: BidderAuthResponse) => {
	clearBidderInvitationSessions();
	localStorage.setItem(APP_TOKEN, response.token);
	localStorage.setItem(USER_ROLE, response.role);
};

export const DEFAULT_BIDDER_AI_DISCLAIMER =
	'AI disclaimer: The AI produced highlights and quote comparisons are intended to help you digest the drawings to quickly provide visibility of scope requirements for this package. These results may not be 100% accurate and it is still the bidders responsibility to thoroughly review the bid documents.';

const normalizedPublishOptions = (value: any): BidderPublishOptions => ({
	aiDisclaimer:
		typeof value?.aiDisclaimer === 'string' && value.aiDisclaimer.trim()
			? value.aiDisclaimer.trim()
			: DEFAULT_BIDDER_AI_DISCLAIMER,
	includeGroupingBulkAnswers:
		typeof value?.includeGroupingBulkAnswers === 'boolean'
			? value.includeGroupingBulkAnswers
			: true,
	includeQuoteReview:
		typeof value?.includeQuoteReview === 'boolean' ? value.includeQuoteReview : true,
	includeScopeLocations:
		typeof value?.includeScopeLocations === 'boolean' ? value.includeScopeLocations : true,
});

const announcePublishOptions = (session: any) => {
	if (typeof window === 'undefined' || !window.dispatchEvent) return;
	window.dispatchEvent(
		new CustomEvent<BidderPublishOptions>(BIDDER_PUBLISH_OPTIONS_EVENT, {
			detail: normalizedPublishOptions(session?.publishOptions),
		}),
	);
};

export const loadPublicInvite = async (token: string) =>
	unwrap<PublicInviteBootstrap>(await publicClient.get(`bid-invites/public/${token}`));

export const loadPublicConsentStatus = async (token: string, email: string, phone: string) =>
	unwrap<{ consents: { sms: boolean; voice: boolean } }>(
		await publicClient.post(`bid-invites/public/${token}/consent-status`, {
			email,
			phone,
		}),
	);

export interface PublicBidderIntakePayload {
	name: string;
	email: string;
	phone: string;
	companyConfirmed: boolean;
	smsConsent: boolean;
	voiceConsent: boolean;
	termsAccepted: boolean;
	termsVersion: string;
	disclosureVersion: string;
	company?: {
		name: string;
		address?: string;
	};
}

export interface BidderRegistrationPayload extends PublicBidderIntakePayload {
	password: string;
}

export const registerBidderAccount = async (token: string, data: BidderRegistrationPayload) => {
	const response = unwrap<BidderAuthResponse>(
		await publicClient.post('bidder/auth/register', { inviteToken: token, ...data }),
	);
	persistBidderAuthentication(response);
	return response;
};

export const loginBidderAccount = async (email: string, password: string, inviteToken?: string) => {
	const response = unwrap<BidderAuthResponse>(
		await publicClient.post('bidder/auth/login', { email, password, inviteToken }),
	);
	persistBidderAuthentication(response);
	return response;
};

export const loadBidderAccountSession = async () =>
	unwrap<BidderAccountSession>(
		await publicClient.get('bidder/session', { headers: bidderAuthHeaders() }),
	);

export const loadBidderInvitations = async (window: 'current' | 'past') => {
	const result = unwrap<{ window: 'current' | 'past'; invitations: BidderAccountInvitation[] }>(
		await publicClient.get('bidder/invitations', {
			headers: bidderAuthHeaders(),
			params: { window },
		}),
	);
	return result.invitations || [];
};

export const openBidderInvitation = async (invitationId: number) =>
	unwrap<{ invitationId: number; sessionToken: string; sessionExpiresAt: string }>(
		await publicClient.post(
			`bidder/invitations/${invitationId}/open`,
			{},
			{ headers: bidderAuthHeaders() },
		),
	);

export const openBidderInvitationByToken = async (inviteToken: string) =>
	unwrap<{ invitationId: number; sessionToken: string; sessionExpiresAt: string }>(
		await publicClient.post(
			'bidder/invitations/open-by-token',
			{ inviteToken },
			{ headers: bidderAuthHeaders() },
		),
	);

export const logoutBidderAccount = async () =>
	unwrap<{ loggedOut: boolean }>(
		await publicClient.post('bidder/auth/logout', {}, { headers: bidderAuthHeaders() }),
	);

export const requestBidderPasswordReset = async (email: string) =>
	unwrap<{ message: string }>(
		await publicClient.post('bidder/auth/password-reset/request', { email }),
	);

export const confirmBidderPasswordReset = async (token: string, password: string) =>
	unwrap<{ passwordReset: boolean }>(
		await publicClient.post('bidder/auth/password-reset/confirm', { token, password }),
	);

export interface BidderAccountSetupPayload {
	token: string;
	password: string;
	termsAccepted: true;
	termsVersion: string;
}

export const confirmBidderContactAccountSetup = async (payload: BidderAccountSetupPayload) => {
	const response = unwrap<BidderAuthResponse>(
		await publicClient.post('bidder/auth/account-setup/confirm', payload),
	);
	persistBidderAuthentication(response);
	return response;
};

export const loadBidderCompanyProfile = async () =>
	unwrap<BidderAccountCompanyProfile>(
		await publicClient.get('bidder/profile', { headers: bidderAuthHeaders() }),
	);

export const updateBidderCompanyProfile = async (
	revision: number,
	company: Partial<VendorCompany>,
) =>
	unwrap<BidderAccountCompanyProfile>(
		await publicClient.put(
			'bidder/profile',
			{ revision, company },
			{ headers: bidderAuthHeaders() },
		),
	);

export interface BidderCompanyContactsResult {
	contacts: VendorContact[];
	revision: number;
	invitationStatus?: 'QUEUED';
}

export const loadBidderCompanyContacts = async () =>
	unwrap<BidderCompanyContactsResult>(
		await publicClient.get('bidder/profile/contacts', { headers: bidderAuthHeaders() }),
	);

export const createBidderCompanyContact = async (
	revision: number,
	contact: Partial<VendorContact>,
) =>
	unwrap<BidderCompanyContactsResult>(
		await publicClient.post(
			'bidder/profile/contacts',
			{ revision, contact },
			{ headers: bidderAuthHeaders() },
		),
	);

export const updateBidderCompanyContact = async (
	revision: number,
	contactId: number,
	contact: Partial<VendorContact>,
) =>
	unwrap<BidderCompanyContactsResult>(
		await publicClient.patch(
			`bidder/profile/contacts/${contactId}`,
			{ revision, contact },
			{ headers: bidderAuthHeaders() },
		),
	);

export const setBidderCompanyContactActive = async (
	revision: number,
	contactId: number,
	active: boolean,
) =>
	unwrap<BidderCompanyContactsResult>(
		await publicClient.post(
			`bidder/profile/contacts/${contactId}/${active ? 'restore' : 'deactivate'}`,
			{ revision },
			{ headers: bidderAuthHeaders() },
		),
	);

export const addBidderWorkArea = async (revision: number, workArea: VendorServiceArea) =>
	unwrap<BidderAccountCompanyProfile>(
		await publicClient.post(
			'bidder/profile/work-areas',
			{ revision, workArea },
			{ headers: bidderAuthHeaders() },
		),
	);

export const deactivateBidderWorkArea = async (revision: number, serviceAreaId: number) =>
	unwrap<BidderAccountCompanyProfile>(
		await publicClient.post(
			`bidder/profile/work-areas/${serviceAreaId}/deactivate`,
			{ revision },
			{ headers: bidderAuthHeaders() },
		),
	);

export const loadBidderWorkPerformedTypes = async () => {
	const result = unwrap<any>(
		await publicClient.get('bidder/work-performed-types', { headers: bidderAuthHeaders() }),
	);
	return (Array.isArray(result)
		? result
		: result.workPerformedTypes || result.rows || []) as WorkPerformedType[];
};

export const loadBidderWorkAreaGeometry = async () =>
	unwrap<VendorWorkAreaGeometry>(
		await publicClient.get('bidder/profile/work-area-geometry', {
			headers: bidderAuthHeaders(),
			params: { _: Date.now() },
		}),
	);

export const submitPublicIntake = async (token: string, data: PublicBidderIntakePayload) =>
	unwrap<any>(await publicClient.post(`bid-invites/public/${token}/intake`, data));

export const loadPublicSession = async (sessionToken: string) => {
	const session = unwrap<BidderSession>(
		await publicClient.get('bid-invites/public/session', {
			headers: sessionHeaders(sessionToken),
		}),
	);
	announcePublishOptions(session);
	return session;
};

export const loadBidderSessionCompanyProfile = async (sessionToken: string) =>
	unwrap<{ company: BidderEditableCompanyProfile }>(
		await publicClient.get('bid-invites/public/session/company-profile', {
			headers: sessionHeaders(sessionToken),
		}),
	);

export const saveBidderSessionCompanyProfile = async (
	sessionToken: string,
	data: Omit<Partial<BidderEditableCompanyProfile>, 'workPerformed'> & {
		expectedVersion: string;
		headquarters?: Partial<VendorLocation>;
		workPerformed?: Array<WorkPerformedType | string>;
	},
) =>
	unwrap<{ company: BidderEditableCompanyProfile }>(
		await publicClient.patch('bid-invites/public/session/company-profile', data, {
			headers: sessionHeaders(sessionToken),
		}),
	);

export const acceptPublicNda = async (sessionToken: string) =>
	unwrap<any>(
		await publicClient.post(
			'bid-invites/public/session/nda-acceptance',
			{ accepted: true },
			{ headers: sessionHeaders(sessionToken) },
		),
	);

export const loadPublicDocuments = async (sessionToken: string) =>
	unwrap<{
		drawings: BidderDocument[];
		specifications: BidderDocument[];
		supplemental: BidderDocument[];
	}>(
		await publicClient.get('bid-invites/public/session/documents', {
			headers: sessionHeaders(sessionToken),
		}),
	);

const responseFileName = (response: any, fallback: string) => {
	const disposition = String(response?.headers?.['content-disposition'] || '');
	const utfName = disposition.match(/filename\*=UTF-8''([^;]+)/i);
	const plainName = disposition.match(/filename="?([^";]+)"?/i);
	const encoded = utfName?.[1] || plainName?.[1];
	if (!encoded) return fallback;
	try {
		return decodeURIComponent(encoded);
	} catch (_error) {
		return encoded;
	}
};

export const downloadPublicDocument = async (sessionToken: string, document: BidderDocument) => {
	const response = await publicClient.get(
		`bid-invites/public/session/documents/${String(
			document.kind || 'DRAWING',
		).toLowerCase()}/${Number(document.id)}/download`,
		{
			headers: sessionHeaders(sessionToken),
			responseType: 'blob',
		},
	);
	return {
		blob: response.data as Blob,
		fileName: responseFileName(response, document.name || 'contract-document.pdf'),
	};
};

export const downloadPublicSupplementalDocument = (
	sessionToken: string,
	documentId: number,
	fileName = '',
) =>
	downloadPublicDocument(sessionToken, {
		id: Number(documentId),
		kind: 'SUPPLEMENTAL',
		name: fileName || `Supplemental-${Number(documentId)}.pdf`,
	});

export const downloadAllPublicDocuments = async (sessionToken: string) => {
	const response = await publicClient.get('bid-invites/public/session/documents/download-all', {
		headers: sessionHeaders(sessionToken),
		responseType: 'blob',
	});
	return {
		blob: response.data as Blob,
		fileName: responseFileName(response, 'Project-Contract-Documents.zip'),
	};
};

export const loadPublicProjectMessages = async (sessionToken: string, focusMessageId?: number) => {
	const body = unwrap<any>(
		await publicClient.get('bid-invites/public/session/project-messages', {
			headers: sessionHeaders(sessionToken),
			params: focusMessageId ? { focusMessageId } : undefined,
		}),
	);
	return {
		messages: (body.messages || body.rows || []) as ProjectMessage[],
		organizationName: String(body.organizationName || 'ScrubPlan').trim() || 'ScrubPlan',
	};
};

export const sendPublicProjectMessage = async (
	sessionToken: string,
	subject: string,
	body: string,
	attachments: File[],
	parentMessageId?: number,
) => {
	const data = new FormData();
	data.append('subject', subject);
	data.append('body', body);
	if (parentMessageId) data.append('parentMessageId', String(parentMessageId));
	attachments.forEach((file) => data.append('attachments', file));
	return unwrap<any>(
		await publicClient.post('bid-invites/public/session/project-messages', data, {
			headers: {
				...sessionHeaders(sessionToken),
				'Content-Type': 'multipart/form-data',
			},
		}),
	);
};

export const loadPublicMessageAttachment = async (
	sessionToken: string,
	messageId: number,
	attachmentId: number,
) =>
	unwrap<{ fileName: string; mimeType?: string; url: string }>(
		await publicClient.get(
			`bid-invites/public/session/project-messages/${messageId}/attachments/${attachmentId}`,
			{ headers: sessionHeaders(sessionToken) },
		),
	);

export const setPublicBidderResponse = async (
	sessionToken: string,
	status: 'BIDDING' | 'NOT_BIDDING' | 'UNDECIDED',
	declineReason?: string,
) =>
	unwrap<any>(
		await publicClient.put(
			'bid-invites/public/session/response',
			{
				status,
				...(status === 'NOT_BIDDING' && declineReason !== undefined
					? { declineReason: declineReason.trim().slice(0, 500) }
					: {}),
			},
			{ headers: sessionHeaders(sessionToken) },
		),
	);

export const setPublicBidderDeclineReason = async (sessionToken: string, declineReason: string) =>
	unwrap<any>(
		await publicClient.put(
			'bid-invites/public/session/decline-reason',
			{ declineReason: declineReason.trim().slice(0, 500) },
			{ headers: sessionHeaders(sessionToken) },
		),
	);
export const publicErrorMessage = (error: any, fallback: string) => {
	const status = error?.response?.status;
	const serverMessage = error?.response?.data?.message;
	const errorCode = error?.response?.data?.details?.code;
	if (
		status === 403 &&
		[
			'INVITE_ONLY_EMAIL_REQUIRED',
			'INVITATION_RECIPIENT_MISMATCH',
			'BIDDER_COMPANY_MISMATCH',
		].includes(errorCode) &&
		serverMessage
	) {
		return serverMessage;
	}
	if (status === 401 || status === 403) {
		return 'This invitation session has expired. Open your original invitation link to continue.';
	}
	if (status === 404 || status === 410) {
		return 'This invitation is invalid, expired, or no longer available.';
	}
	return error?.response?.data?.message || error?.message || fallback;
};
